/**
 * Marketing-Cockpit Stufe 1 (#865c, Konzept docs/konzepte/865-marketing-modul-v2.md): Verträge für Inbox/Cursor (n8n
 * `wf_portal_marketing_shopify_1` / `wf_portal_marketing_semrush_1`) und die Lese-API `/api/marketing/v1/*`.
 * Nur Aggregate (Tag/Woche), keine Personendaten. Datum immer `YYYY-MM-DD` (Tag Europe/Berlin).
 */

export const MKT_API = '/api/marketing/v1';
export const MKT_INBOX_PFAD = '/api/integration/inbox/marketing';
export const MKT_CURSOR_PFAD = '/api/integration/cursor/marketing';
export const MKT_BATCH_MAX = 500;
/** SEMrush-Budget je Kalendermonat (Entscheidung Matthias 04.11.2026) */
export const MKT_SEMRUSH_UNITS_BUDGET = 15000;
/** Lease eines Laufs; ein zweiter Lauf desselben Systems wird bis dahin abgewiesen */
export const MKT_LEASE_STUNDEN = 2;

export const MKT_SYSTEME = ['shopify', 'semrush', 'ga4', 'google_ads', 'microsoft_ads'] as const;
export type MktSystem = (typeof MKT_SYSTEME)[number];
/** #895: fehler beendet einen Lauf (phase fehler, Lease frei); ads-Systeme schreiben ads_tag mit quelle google | microsoft */
export const MKT_KINDS: Record<MktSystem, readonly MktInboxKind[]> = {
  shopify: ['kennzahl', 'herkunft', 'complete', 'fehler'],
  semrush: ['seo', 'wettbewerber', 'complete', 'fehler'],
  ga4: ['ga4', 'complete', 'fehler'],
  google_ads: ['ads', 'complete', 'fehler'],
  microsoft_ads: ['ads', 'complete', 'fehler'],
};
export const MKT_INBOX_KINDS = ['kennzahl', 'herkunft', 'seo', 'wettbewerber', 'ga4', 'ads', 'complete', 'fehler'] as const;
export const MKT_GA4_EBENEN = ['tag', 'kanal', 'landingpage'] as const;
export type MktGa4Ebene = (typeof MKT_GA4_EBENEN)[number];
export type MktAdsQuelle = 'google' | 'microsoft';
/** Ads-Quelle aus dem System */
export const MKT_ADS_QUELLE: Partial<Record<MktSystem, MktAdsQuelle>> = { google_ads: 'google', microsoft_ads: 'microsoft' };
/** complete.tageErsetzt: höchstens so viele Tage */
export const MKT_TAGE_ERSETZT_MAX = 40;
export const MKT_FEHLER_MELDUNG_MAX = 2000;
export type MktInboxKind = (typeof MKT_INBOX_KINDS)[number];
export const MKT_HERKUNFT_BASEN = ['session', 'order_last', 'order_first'] as const;
export type MktHerkunftBasis = (typeof MKT_HERKUNFT_BASEN)[number];
export const MKT_SEO_LISTEN = ['top', 'chance'] as const;
export type MktSeoListe = (typeof MKT_SEO_LISTEN)[number];

/** KI-Assistenten-Regel (Konzept v2 P4), Abgleich exakt auf utm_source oder referrer_name (klein) — Untergrenze */
export const MKT_KI_REGEL_VERSION = '2026-11-04';
export const MKT_KI_ASSISTENTEN: readonly { name: string; muster: readonly string[] }[] = [
  { name: 'ChatGPT', muster: ['chatgpt.com', 'chat.openai.com', 'chatgpt', 'openai'] },
  { name: 'Perplexity', muster: ['perplexity.ai', 'perplexity'] },
  { name: 'Copilot', muster: ['copilot.com', 'copilot.microsoft.com', 'copilot'] },
  { name: 'Gemini', muster: ['gemini.google.com', 'gemini'] },
  { name: 'Claude', muster: ['claude.ai', 'claude'] },
];

// ── Inbox ────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** kennzahl (shopify): fehlende Felder bleiben unverändert (Sessions und Sales kommen aus getrennten ShopifyQL-Abfragen) */
export interface MktKennzahlZeile { tag: string; sessions?: number | null; orders?: number | null; umsatz?: number | null; neueKunden?: number | null }
/** herkunft (shopify): eine Zeile = vollständiges Aggregat ihres Schlüssels (Tag × Basis × Dimensionen × ohneJourney) */
export interface MktHerkunftZeile {
  tag: string;
  basis: MktHerkunftBasis;
  kanalgruppe?: string | null;
  referrerName?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  /** nur basis order_*: Orders ohne customerJourneySummary (Dimensionen leer) */
  ohneJourney?: boolean;
  sessions?: number | null;
  orders?: number | null;
  umsatz?: number | null;
}
/** seo (semrush): Wochen-Snapshot, woche = Montag */
export interface MktSeoZeile { woche: string; liste: MktSeoListe; keyword: string; position: number; vorposition?: number | null; suchvolumen?: number | null; trafficAnteil?: number | null; url?: string | null }
export interface MktWettbewerberZeile { woche: string; domain: string; relevanz?: number | null; gemeinsameKeywords?: number | null; orgKeywords?: number | null; orgTraffic?: number | null }

/** ga4 (#895): ebene tag = Tagessumme (Nutzer nicht additiv), kanal = kanalgruppe Pflicht, landingpage = Pfad Pflicht (ohne Query) */
export interface MktGa4Zeile { tag: string; ebene: MktGa4Ebene; kanalgruppe?: string | null; landingpage?: string | null; sessions: number; nutzer?: number | null; neueNutzer?: number | null; engagierteSessions?: number | null }
/** ads (#895, google_ads | microsoft_ads): Kosten in EUR (2 Nachkommastellen), Schlüssel tag + kampagneId */
export interface MktAdsZeile { tag: string; kampagneId: string; kampagne: string; kosten: number; klicks: number; impressionen: number; conversions: number; conversionWert?: number | null; waehrung?: 'EUR' }

export type MktInboxRequest =
  | { runId: string; kind: 'kennzahl'; seite: number; observedAt: string; zeilen: MktKennzahlZeile[] }
  | { runId: string; kind: 'herkunft'; seite: number; observedAt: string; zeilen: MktHerkunftZeile[] }
  | { runId: string; kind: 'seo'; seite: number; observedAt: string; zeilen: MktSeoZeile[] }
  | { runId: string; kind: 'wettbewerber'; seite: number; observedAt: string; zeilen: MktWettbewerberZeile[] }
  | { runId: string; kind: 'ga4'; seite: number; observedAt: string; zeilen: MktGa4Zeile[] }
  | { runId: string; kind: 'ads'; seite: number; observedAt: string; zeilen: MktAdsZeile[] }
  /**
   * Abschluss: Datenstand veröffentlichen, Lease freigeben; semrush meldet die verbrauchten Units des Laufs. tageErsetzt (#895, ≤ 40):
   * Zeilen des Systems an diesen Tagen, die nicht aus diesem runId stammen, werden auf 0 gesetzt (UPDATE, kein DELETE).
   */
  | { runId: string; kind: 'complete'; seite: 0; datenstand: string; unitsVerbraucht?: number; tageErsetzt?: string[] }
  /** #895: Lauf mit Fehler beenden (phase fehler, Lease frei, last_error = meldung) */
  | { runId: string; kind: 'fehler'; seite: number; meldung: string };

export interface MktInboxQuittung { system: MktSystem; runId: string; kind: MktInboxKind; seite: number; status: 'ok' | 'duplicate'; angenommen: number; quittiertAt: string }
export interface MktInboxFehler { statusCode: number; code: string; message: string }

export interface MktCursor {
  system: MktSystem;
  phase: 'bereit' | 'laeuft' | 'fehler';
  runId: string | null;
  lockedUntil: string | null;
  datenstand: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  /** shopify: jüngster Tag in kennzahl_tag; semrush: jüngste Woche in seo_snapshot; ga4: ga4_tag; google_ads/microsoft_ads: ads_tag der Quelle */
  letzterStichtag: string | null;
  /** nur semrush: Units im laufenden Monat (Europe/Berlin) — n8n startet nicht, wenn unitsRest < Bedarf */
  units: { monat: string; verbraucht: number; budget: number; rest: number } | null;
}

// ── Lese-API ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface MktZeitraum { von: string; bis: string; tage: number }
/**
 * Vergleichsfenster: gleich lang, um 7 bzw. 28 Tage verschoben (gleiche Wochentage); #895g vorjahr um 364 Tage (52 Wochen, gleiche
 * Wochentage) — null, wenn im Vorjahresfenster noch keine Daten liegen (Historie beginnt später) → UI zeigt „—“.
 */
export interface MktVergleich { vorwoche: MktZeitraum; vormonat: MktZeitraum; vorjahr: MktZeitraum | null }
/** Δ in Prozent (gerundet auf 0,1), null = Vergleichswert 0 oder fehlt (vorjahr null auch ohne Vorjahresdaten) */
export interface MktDelta { vorwoche: number | null; vormonat: number | null; vorjahr: number | null }
/**
 * #895g Zeitraum-Presets der Lese-API (`?preset=`), rollierend bis `bis` (Standard gestern): 7 T, 30 T, Quartal = 91 T (13 Wochen),
 * Jahr = 364 T (52 Wochen), gesamt = ab erstem gelieferten Tag. Alternativ `von`/`bis` frei, ohne Obergrenze (ewige Historie).
 */
export const MKT_PRESETS = [
  { preset: '7t', label: '7 Tage', tage: 7 },
  { preset: '30t', label: '30 Tage', tage: 30 },
  { preset: 'quartal', label: 'Quartal', tage: 91 },
  { preset: 'jahr', label: 'Jahr', tage: 364 },
  { preset: 'gesamt', label: 'Gesamt', tage: null },
] as const;
export type MktPreset = (typeof MKT_PRESETS)[number]['preset'];

export interface MktKennzahlen { sessions: number; orders: number; umsatz: number; neueKunden: number; conversion: number | null; warenkorb: number | null }
export interface MktTagesuebersicht {
  zeitraum: MktZeitraum;
  vergleich: MktVergleich;
  summe: MktKennzahlen;
  vorwoche: MktKennzahlen;
  vormonat: MktKennzahlen;
  /** #895g null = im Vorjahresfenster noch keine Daten */
  vorjahr: MktKennzahlen | null;
  delta: Record<keyof MktKennzahlen, MktDelta>;
  tage: { tag: string; sessions: number | null; orders: number | null; umsatz: number | null; neueKunden: number | null; sessionsVorwoche: number | null }[];
  datenstand: string | null;
}

export interface MktDimension { kanalgruppe: string; referrerName: string; utmSource: string; utmMedium: string; utmCampaign: string; utmContent: string }
export interface MktHerkunftSession extends MktDimension { sessions: number; vorwoche: number; vormonat: number; vorjahr: number; delta: MktDelta }
export interface MktHerkunftOrder extends MktDimension { orders: number; umsatz: number; vorwoche: { orders: number; umsatz: number }; vormonat: { orders: number; umsatz: number }; vorjahr: { orders: number; umsatz: number }; delta: MktDelta }
export interface MktHerkunft {
  zeitraum: MktZeitraum;
  vergleich: MktVergleich;
  sessions: { summe: number; zeilen: MktHerkunftSession[] };
  orders: {
    modell: 'last' | 'first';
    summe: { orders: number; umsatz: number };
    /** Orders ohne Journey — immer eigene Zeile */
    ohneJourney: { orders: number; umsatz: number };
    /** Anteil Orders mit Journey an allen Orders (0–1), null = keine Orders */
    attributionsquote: number | null;
    zeilen: MktHerkunftOrder[];
  };
  datenstand: string | null;
}

export interface MktKiAssistent { name: string; sessions: number; orders: number; umsatz: number; vorwoche: { sessions: number; orders: number; umsatz: number }; delta: MktDelta }
export interface MktKi {
  zeitraum: MktZeitraum;
  vergleich: MktVergleich;
  regelVersion: string;
  /** immer true: KI-Apps ohne Referrer/UTM zählen als Direkt; KI-Crawler sind auf Shopify-Hosting nicht messbar */
  untergrenze: true;
  summe: { sessions: number; orders: number; umsatz: number };
  anteilSessions: number | null;
  assistenten: MktKiAssistent[];
  datenstand: string | null;
}

export type MktSeoStatus = 'besser' | 'schlechter' | 'gleich' | 'neu';
/** #895g vorjahrPosition aus dem Snapshot `vorjahrWoche`; deltaVorjahr = vorjahrPosition − position (positiv = besser), sonst null */
export interface MktSeoKeyword { keyword: string; position: number; vorposition: number | null; delta: number | null; status: MktSeoStatus; suchvolumen: number | null; trafficAnteil: number | null; url: string | null; vorjahrPosition: number | null; deltaVorjahr: number | null }
export interface MktSeoWettbewerber { domain: string; relevanz: number | null; gemeinsameKeywords: number | null; orgKeywords: number | null; orgTraffic: number | null; deltaGemeinsame: number | null }
export interface MktSeo {
  woche: string | null;
  /** Vergleichs-Snapshot ≥ 4 Wochen älter (eigene Snapshots; sonst Vorposition aus der Lieferung) */
  vergleichWoche: string | null;
  /** #895g jüngste Woche ≤ woche − 364 Tage (höchstens 1 Woche älter), sonst null */
  vorjahrWoche: string | null;
  top: MktSeoKeyword[];
  chancen: MktSeoKeyword[];
  verloren: { keyword: string; vorposition: number }[];
  zaehler: { besser: number; schlechter: number; gleich: number; neu: number };
  wettbewerber: MktSeoWettbewerber[];
  units: { monat: string; verbraucht: number; budget: number; rest: number } | null;
  datenstand: string | null;
}

// ── Tagesauswertung (#895) ───────────────────────────────────────────────────────────────────────────────────────────
/** Wert eines Tages mit Vergleichstagen (−1 / −7 / −28 / #895g −364); null = Quelle hat für den Tag nichts geliefert. delta % auf 0,1 */
export interface MktTagWert { wert: number | null; vortag: number | null; vorwoche: number | null; vormonat: number | null; vorjahr: number | null; delta: { vortag: number | null; vorwoche: number | null; vormonat: number | null; vorjahr: number | null } }
export const MKT_TAG_KENNZAHLEN = [
  'sessionsShopify', 'sessionsGa4', 'nutzerGa4', 'orders', 'umsatz', 'bestellwert', 'neueKunden', 'adsKosten', 'adsKlicks', 'adsConversions', 'roas', 'mer', 'kiSessions',
] as const;
export type MktTagKennzahl = (typeof MKT_TAG_KENNZAHLEN)[number];
export const MKT_QUELLEN_SYSTEME = ['shopify', 'ga4', 'google_ads', 'microsoft_ads', 'semrush', 'meta', 'youtube', 'pinterest', 'tiktok', 'awin'] as const;
export type MktQuelleSystem = (typeof MKT_QUELLEN_SYSTEME)[number];
export const MKT_QUELLEN_STATUS = ['aktuell', 'veraltet', 'fehler', 'leer', 'wartet_freigabe', 'inaktiv'] as const;
export type MktQuelleStatus = (typeof MKT_QUELLEN_STATUS)[number];
export interface MktQuelle { system: MktQuelleSystem; name: string; status: MktQuelleStatus; datenstand: string | null; letzterTag: string | null; lastError: string | null; hinweis: string | null }
/** Feste Quellen ohne Anbindung — immer status wartet_freigabe; Hinweis in Klartext + Verweis auf die Freigaben-Checkliste (Blocktitel), ohne interne Ticketnummern (#895d) */
export const MKT_FREIGABE_PRAEFIX = 'Freigabe durch Brückner nötig:';
export const MKT_QUELLEN_WARTEND: readonly { system: MktQuelleSystem; name: string; hinweis: string }[] = [
  { system: 'meta', name: 'Instagram/Facebook', hinweis: `${MKT_FREIGABE_PRAEFIX} Meta-App mit System-Nutzer und Dauer-Token (App-Review) · siehe Freigaben-Checkliste „Meta Graph: App „API Universe“ → System-User, Permanent-Token, Scopes, App-Review“` },
  { system: 'youtube', name: 'YouTube', hinweis: `${MKT_FREIGABE_PRAEFIX} YouTube-APIs im Google-Cloud-Projekt freischalten · siehe Freigaben-Checkliste „YouTube Data API (GCP)“` },
  { system: 'pinterest', name: 'Pinterest', hinweis: `${MKT_FREIGABE_PRAEFIX} Pinterest-API-Zugang (Testzugang ausstehend) · siehe Freigaben-Checkliste „Pinterest API“` },
  { system: 'tiktok', name: 'TikTok', hinweis: `${MKT_FREIGABE_PRAEFIX} TikTok-Produktivzugang (bisher nur Sandbox, Prüfung offen) · siehe Freigaben-Checkliste „TikTok for Business API“` },
  { system: 'awin', name: 'Awin', hinweis: `${MKT_FREIGABE_PRAEFIX} Awin-Berichtszugriff (derzeit verweigert, Support angefragt) · siehe Freigaben-Checkliste „Awin Publisher/Advertiser API-Token“` },
];
export interface MktTopHerkunft { kanalgruppe: string; referrerName: string; sessions: number; anteil: number | null; vorwoche: number; deltaVorwoche: number | null }
export interface MktTagesauswertung {
  tag: string;
  /** −1 / −7 / −28 Tage */
  /** vorjahr = Tag −364 (gleicher Wochentag); null, wenn die Historie dort noch nicht beginnt */
  vergleich: { vortag: string; vorwoche: string; vormonat: string; vorjahr: string | null };
  /** bestellwert = umsatz/orders · adsKosten = google + microsoft · roas = Σ conversionWert / Σ kosten · mer = Shopify-Umsatz / Ads-Kosten */
  kennzahlen: Record<MktTagKennzahl, MktTagWert>;
  /** ≤ 5, basis session, gruppiert kanalgruppe + referrer_name */
  topHerkunft: MktTopHerkunft[];
  /** Regel MKT_KI_ASSISTENTEN (Untergrenze) */
  ki: { sessions: number; assistenten: { name: string; sessions: number }[] };
  quellen: MktQuelle[];
}
